import { useContext, useEffect, useMemo, useState } from 'react';
import ImpersonatorContext from '../../../../contexts/ImpersonatorContext';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';

export const usePersistedGates = () => {
  const [gates, setGates] = useState<FleetOps.AccountFeatureGates>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { accountRef } = useContext(AccountPickerContext);

  useEffect(() => {
    const listener = accountRef.onSnapshot((snapshot) => {
      const newAccountData = snapshot.data();
      if (newAccountData) {
        const gates = newAccountData.featureGates
          ? (newAccountData.featureGates as FleetOps.AccountFeatureGates)
          : ({} as FleetOps.AccountFeatureGates);
        setGates(gates);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setGates(undefined);
      return;
    });

    return () => {
      listener();
    };
  }, [accountRef]);

  return {
    gates,
    isLoadingGates: isLoading,
  };
};

const useFeatureGates = () => {
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { isFleetOpsStaff } = useContext(CurrentUserContext);
  const { gates, isLoadingGates } = usePersistedGates();

  // Derived in the render the account's gates load in, not with useState +
  // useEffect: that left every gate off for one more render, and the providers
  // below have mounted by then (BoardsProvider, for one, reads performance).
  // Everything is off until the gates load, staff overrides included.
  return useMemo(() => {
    const loadedGates: Partial<FleetOps.AccountFeatureGates> = gates ?? {};
    const isStaff =
      !!gates && !!isFleetOpsStaff && impersonatorId === undefined;

    return {
      isGoalsEnabled: !!loadedGates.goals || isStaff,
      isMultipleCardsEnabled: !!loadedGates.multipleCards,
      isDriverBonusSummaryEnabled: !!loadedGates.driverBonusSummary || isStaff,
      isSelfServeEnabled: !!loadedGates.selfServe || isStaff,
      isMicrosoftSsoEnabled: !!loadedGates.microsoftSso,
      isEntityDetailsEnabled: !!loadedGates.entityDetails,
      isActivityCostsEnabled: !!loadedGates.activityCosts,
      isFileUploadEnabled: !!loadedGates.fileUpload,
      isCustomerAwardsTrackerEnabled: !!loadedGates.customerAwardsTracker,
      isManualKpiPermissionManagementEnabled:
        !!loadedGates.manualKpiPermissionManagement,
      isDriverBonusEnabled: !!loadedGates.driverBonus,
      isPerformanceEnabled: !!loadedGates.performance,
      isTemplatesEnabled: !!loadedGates.templates,
      isViewerRoleAssignmentEnabled: !!loadedGates.viewerRoleAssignment,
      isPortalsSEPreviewEnabled: !!loadedGates.portalsSEPreview,
      isPortalsUIPerUserToggleEnabled: !!loadedGates.portalsUIPerUserToggle,
      isPortalsAccountWideToggleEnabled: !!loadedGates.portalsAccountWideToggle,
      isV3PublicContentEnabled: !!loadedGates.v3PublicContent,
      isDriverSetupAndAppAccessEnabled:
        !!loadedGates.driverSetupAndAppAccess || isStaff,
      isDriverBonusExclusionEnabled:
        !!loadedGates.driverBonusExclusion || isStaff,
      isDriverSmsInviteEnabled: !!loadedGates.driverSmsInvite,
      // No staff override: SEs configure it first, then open the gate.
      isAdditionalPaymentsEnabled: !!loadedGates.additionalPayments,
      isFiscalPeriodsEnabled: !!loadedGates.fiscalPeriods || isStaff,
      isDriverAppEnabled: !!loadedGates.driverAppEnabled,
      isScheduledReportsEnabled: !!loadedGates.scheduledReports || isStaff,
      isAiInsightsChatEnabled: !!loadedGates.aiInsightsChat || isStaff,
      isDriverAppAdminAccessEnabled: !!loadedGates.driverAppAdminAccess,
      isDriverAppEmbedPreviewEnabled: !!loadedGates.driverAppEmbedPreview,
      isDriverAppAutoInviteEnabled: !!loadedGates.driverAppAutoInvite,
      isStudioEnabled: !!loadedGates.studio,
      // The account's setting only: who sees DataTable on a gated account is
      // each surface's rollout stage (useIsDataTable)
      isDataTableEnabled: !!loadedGates.dataTable,
      isLoading: isLoadingGates,
    };
  }, [gates, impersonatorId, isFleetOpsStaff, isLoadingGates]);
};

export default useFeatureGates;
